// Admin "PayFast Payments Monitor"
//
// One screen to audit every PayFast reference end-to-end:
//   • Top: recent ITN trace log with signature / VALIDATE / grant status
//   • Middle: lookup any reference and see the order + grant verification
//   • Bottom: background reconciliation run history with a manual trigger
//
// Data comes from `payments-monitor.functions.ts` server functions which
// already enforce `assertAdmin`. We refetch the ITN log on a short interval
// so failures surface within seconds.
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  CheckCircle2,
  XCircle,
  ShieldCheck,
  ShieldAlert,
  RefreshCw,
  Search,
  PlayCircle,
} from "lucide-react";
import {
  adminListItnLogs,
  adminGetReferenceTrace,
  adminListReconciliationRuns,
  adminTriggerReconciliation,
} from "@/lib/api/payments-monitor.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/payments-monitor",
)({
  head: () => ({
    meta: [
      { title: "PayFast payments monitor — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: MonitorPage,
});

function Pill({
  ok,
  label,
}: {
  ok: boolean | null | undefined;
  label: string;
}) {
  const tone =
    ok === true
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : ok === false
        ? "bg-rose-50 text-rose-800 border-rose-200"
        : "bg-muted text-muted-foreground border-border";
  const Icon = ok === true ? CheckCircle2 : ok === false ? XCircle : ShieldAlert;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs ${tone}`}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

function MonitorPage() {
  const listLogs = useServerFn(adminListItnLogs);
  const getTrace = useServerFn(adminGetReferenceTrace);
  const listRuns = useServerFn(adminListReconciliationRuns);
  const triggerReconcile = useServerFn(adminTriggerReconciliation);
  const qc = useQueryClient();

  const [filter, setFilter] = useState({
    reference: "",
    signatureValid: "all" as "all" | "yes" | "no",
    grantStatus: "" as "" | "granted" | "already-granted" | "skipped" | "n/a",
  });

  const logsQ = useQuery({
    queryKey: ["itn-logs", filter],
    queryFn: () =>
      listLogs({
        data: {
          reference: filter.reference || undefined,
          signatureValid: filter.signatureValid,
          grantStatus: filter.grantStatus || undefined,
          page: 1,
          pageSize: 50,
        },
      }),
    refetchInterval: 15000,
  });

  const runsQ = useQuery({
    queryKey: ["reconcile-runs"],
    queryFn: () => listRuns({ data: { page: 1, pageSize: 10 } }),
    refetchInterval: 60000,
  });

  const [lookup, setLookup] = useState("");
  const [trace, setTrace] = useState<any>(null);
  const traceM = useMutation({
    mutationFn: (reference: string) => getTrace({ data: { reference } }),
    onSuccess: (res) => setTrace(res),
    onError: (e: any) => toast.error(e?.message ?? "Lookup failed"),
  });

  const reconcileM = useMutation({
    mutationFn: () => triggerReconcile({}),
    onSuccess: (s: any) => {
      toast.success(
        `Reconciled: ${s.grant_mismatch.fixed} fixed, ${s.grant_mismatch.failed} failed`,
      );
      qc.invalidateQueries({ queryKey: ["reconcile-runs"] });
      qc.invalidateQueries({ queryKey: ["itn-logs"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Reconcile failed"),
  });

  const rows: any[] = (logsQ.data as any)?.rows ?? [];
  const runs: any[] = (runsQ.data as any)?.rows ?? [];

  return (
    <div className="mx-auto max-w-7xl space-y-8 px-4 py-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">PayFast payments monitor</h1>
          <p className="text-sm text-muted-foreground">
            Per-reference signature, VALIDATE, idempotency, credit grant and
            subscription activation results — refreshes every 15s.
          </p>
        </div>
        <Button
          onClick={() => reconcileM.mutate()}
          disabled={reconcileM.isPending}
        >
          <PlayCircle className="mr-2 h-4 w-4" />
          {reconcileM.isPending ? "Running…" : "Run reconciliation now"}
        </Button>
      </header>

      {/* ---- ITN trace log ---- */}
      <section className="rounded-lg border bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b p-4">
          <Input
            placeholder="Filter by reference…"
            value={filter.reference}
            onChange={(e) =>
              setFilter((f) => ({ ...f, reference: e.target.value }))
            }
            className="w-64"
          />
          <select
            value={filter.signatureValid}
            onChange={(e) =>
              setFilter((f) => ({
                ...f,
                signatureValid: e.target.value as any,
              }))
            }
            className="rounded border px-2 py-1 text-sm"
          >
            <option value="all">All signatures</option>
            <option value="yes">Signature valid</option>
            <option value="no">Signature invalid</option>
          </select>
          <select
            value={filter.grantStatus}
            onChange={(e) =>
              setFilter((f) => ({ ...f, grantStatus: e.target.value as any }))
            }
            className="rounded border px-2 py-1 text-sm"
          >
            <option value="">Any grant status</option>
            <option value="granted">Granted</option>
            <option value="already-granted">Already granted</option>
            <option value="skipped">Skipped</option>
            <option value="n/a">n/a</option>
          </select>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => logsQ.refetch()}
            disabled={logsQ.isFetching}
          >
            <RefreshCw
              className={`mr-1 h-4 w-4 ${logsQ.isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Reference</th>
                <th className="px-3 py-2 text-left">PF id</th>
                <th className="px-3 py-2 text-left">Sig</th>
                <th className="px-3 py-2 text-left">VALIDATE</th>
                <th className="px-3 py-2 text-left">Match</th>
                <th className="px-3 py-2 text-left">Grant</th>
                <th className="px-3 py-2 text-right">Attempt</th>
                <th className="px-3 py-2 text-right">ms</th>
              </tr>
            </thead>
            <tbody>
              {logsQ.isLoading && (
                <tr>
                  <td colSpan={9} className="p-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {!logsQ.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-6 text-center text-muted-foreground">
                    No ITN requests match these filters.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="cursor-pointer border-t hover:bg-muted/30"
                  onClick={() => {
                    setLookup(r.reference ?? "");
                    if (r.reference) traceM.mutate(r.reference);
                  }}
                >
                  <td className="px-3 py-2 whitespace-nowrap text-xs">
                    {new Date(r.created_at).toLocaleString("en-ZA")}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {r.reference ?? "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {r.pf_payment_id ?? "—"}
                  </td>
                  <td className="px-3 py-2">
                    <Pill
                      ok={r.signature_valid}
                      label={
                        r.signature_valid === true
                          ? "valid"
                          : r.signature_valid === false
                            ? "invalid"
                            : "—"
                      }
                    />
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.server_validate_result}
                  </td>
                  <td className="px-3 py-2 text-xs">{r.match_kind ?? "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.grant_status}
                    {r.grant_error && (
                      <span className="ml-1 text-rose-700">
                        ({r.grant_error.slice(0, 40)})
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-xs">{r.attempt_no}</td>
                  <td className="px-3 py-2 text-right text-xs">
                    {r.processing_ms}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ---- Reference lookup ---- */}
      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-2 text-lg font-semibold">Reference trace</h2>
        <div className="flex gap-2">
          <Input
            placeholder="Paste a PayFast reference (e.g. CR-… / CV-… / SUB-…)"
            value={lookup}
            onChange={(e) => setLookup(e.target.value)}
            className="max-w-md"
          />
          <Button
            onClick={() => lookup.trim() && traceM.mutate(lookup.trim())}
            disabled={traceM.isPending || !lookup.trim()}
          >
            <Search className="mr-1 h-4 w-4" />
            Look up
          </Button>
        </div>

        {trace && (
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <div className="rounded border p-3">
              <h3 className="mb-2 text-sm font-semibold">Order</h3>
              <pre className="overflow-x-auto text-xs">
                {JSON.stringify(trace.order, null, 2)}
              </pre>
            </div>
            <div className="rounded border p-3">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                Grant verification{" "}
                <Pill
                  ok={trace.grant.satisfied}
                  label={
                    trace.grant.kind
                      ? trace.grant.satisfied
                        ? `${trace.grant.kind} ✓`
                        : `${trace.grant.kind} missing`
                      : "no order"
                  }
                />
              </h3>
              <pre className="overflow-x-auto text-xs">
                {JSON.stringify(trace.grant.detail, null, 2)}
              </pre>
            </div>
            <div className="rounded border p-3 lg:col-span-2">
              <h3 className="mb-2 text-sm font-semibold">
                ITN attempts ({trace.itnLogs.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1 text-left">When</th>
                      <th className="px-2 py-1 text-left">Sig</th>
                      <th className="px-2 py-1 text-left">VALIDATE</th>
                      <th className="px-2 py-1 text-left">Grant</th>
                      <th className="px-2 py-1 text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trace.itnLogs.map((l: any) => (
                      <tr key={l.id} className="border-t">
                        <td className="px-2 py-1 whitespace-nowrap">
                          {new Date(l.created_at).toLocaleString("en-ZA")}
                        </td>
                        <td className="px-2 py-1">
                          {String(l.signature_valid)}
                        </td>
                        <td className="px-2 py-1">{l.server_validate_result}</td>
                        <td className="px-2 py-1">
                          {l.grant_status}
                          {l.grant_error && (
                            <span className="text-rose-700">
                              {" "}
                              · {l.grant_error.slice(0, 60)}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1">{l.notes ?? ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="rounded border p-3 lg:col-span-2">
              <h3 className="mb-2 text-sm font-semibold">
                Idempotent events ({trace.events.length})
              </h3>
              <ul className="space-y-1 text-xs">
                {trace.events.map((e: any) => (
                  <li key={e.id} className="flex items-center gap-2">
                    <ShieldCheck
                      className={`h-3 w-3 ${e.processed ? "text-emerald-600" : "text-amber-600"}`}
                    />
                    <span className="font-mono">{e.event_type}</span>
                    <span className="text-muted-foreground">
                      {new Date(e.created_at).toLocaleString("en-ZA")}
                    </span>
                    {e.error_message && (
                      <span className="text-rose-700">{e.error_message}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </section>

      {/* ---- Reconciliation runs ---- */}
      <section className="rounded-lg border bg-card">
        <div className="border-b p-4">
          <h2 className="text-lg font-semibold">
            Background reconciliation runs
          </h2>
          <p className="text-xs text-muted-foreground">
            Cross-checks PayFast-paid orders against credit_ledger,
            cv_credit_grants, user_subscriptions and student_module_enrollments.
            Replays any missing grants automatically.
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Started</th>
              <th className="px-3 py-2 text-right">Scanned</th>
              <th className="px-3 py-2 text-right">Mismatches</th>
              <th className="px-3 py-2 text-right">Fixed</th>
              <th className="px-3 py-2 text-right">Failures</th>
              <th className="px-3 py-2 text-right">Duration</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="p-6 text-center text-muted-foreground"
                >
                  No reconciliation runs yet. Trigger one with the button above.
                </td>
              </tr>
            )}
            {runs.map((r) => {
              const durationMs =
                r.finished_at && r.started_at
                  ? new Date(r.finished_at).getTime() -
                    new Date(r.started_at).getTime()
                  : null;
              return (
                <tr key={r.id} className="border-t">
                  <td className="px-3 py-2 text-xs">
                    {new Date(r.started_at).toLocaleString("en-ZA")}
                  </td>
                  <td className="px-3 py-2 text-right">{r.scanned_events}</td>
                  <td className="px-3 py-2 text-right">{r.mismatches_found}</td>
                  <td className="px-3 py-2 text-right text-emerald-700">
                    {r.auto_fixed}
                  </td>
                  <td
                    className={`px-3 py-2 text-right ${r.fix_failures > 0 ? "text-rose-700" : ""}`}
                  >
                    {r.fix_failures}
                  </td>
                  <td className="px-3 py-2 text-right text-xs">
                    {durationMs != null ? `${durationMs} ms` : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}
